import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Globe } from "lucide-react";
import { Control, FieldPath, FieldValues } from "react-hook-form";
import { useState } from "react";
import { countries as countryCodes } from "country-code-lookup";

// Convert country codes to the format we need for geo-blocking
const countries = countryCodes
  .map((country: any) => ({
    code: country.iso2,
    name: country.country
  }))
  .filter((country: any) => country.code && country.name) // Filter out any invalid entries
  .sort((a: any, b: any) => {
    // Define priority countries in the desired order
    const priorityCountries = [
      'US', 'GB', 'CA', 'CN', 'IN', 'SG', 'JP', 'KR', 'TH', 'VN', 'IR', 'KP', 'SY', 'CU', 'SD', 'SS'
    ];

    const aIndex = priorityCountries.indexOf(a.code);
    const bIndex = priorityCountries.indexOf(b.code);

    // If both countries are in the priority list, sort by their position in the list
    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex;
    }

    // If only one country is in the priority list, it goes first
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;

    // If neither country is in the priority list, sort alphabetically
    return a.name.localeCompare(b.name);
  });

// Type for country objects
type CountryOption = {
  code: string;
  name: string;
};

interface BlockedCountriesFieldProps<T extends FieldValues> {
  control: Control<T>;
  name: FieldPath<T>;
  showHeader?: boolean;
  showSeparator?: boolean;
}

export function BlockedCountriesField<T extends FieldValues>({
  control,
  name,
  showHeader = true,
}: BlockedCountriesFieldProps<T>) {
  return (
    <div className="space-y-4">
      {showHeader && (
        <h3 className="text-lg font-medium flex items-center gap-2">
          <Globe className="h-4 w-4" />
          Geographic Restrictions (Optional)
        </h3>
      )}

      <FormField
        control={control}
        name={name}
        render={({ field }) => {
          const [searchTerm, setSearchTerm] = useState("");

          const filteredCountries = countries
            .filter((country: CountryOption) =>
              country.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
              country.code.toLowerCase().includes(searchTerm.toLowerCase())
            )
            .sort((a: CountryOption, b: CountryOption) => {
              const aSelected = field.value?.includes(a.code) || false;
              const bSelected = field.value?.includes(b.code) || false;

              // If both are selected or both are unselected, maintain their relative order
              if (aSelected === bSelected) {
                // Find their original positions in the countries array to preserve relative order
                const aIndex = countries.findIndex(c => c.code === a.code);
                const bIndex = countries.findIndex(c => c.code === b.code);
                return aIndex - bIndex;
              }

              // Selected countries go first
              return aSelected ? -1 : 1;
            });

          return (
            <FormItem>
              <FormLabel>Restricted Countries</FormLabel>
              <FormDescription>
                Select countries where this bench sale will be restricted.
                Users from these countries will be restricted from purchasing through the myth.finance frontend.
              </FormDescription>
              <FormControl>
                <div className="space-y-3">
                  {/* Search Filter */}
                  <div className="relative">
                    <Input
                      placeholder="Search countries..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pr-8"
                    />
                    {searchTerm && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                        onClick={() => setSearchTerm("")}
                      >
                        ×
                      </Button>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto border rounded-md p-3">
                    {filteredCountries.length > 0 ? (
                      filteredCountries.map((country: CountryOption) => (
                        <div
                          key={country.code}
                          className="flex items-center space-x-2 select-none"
                        >
                          <input
                            type="checkbox"
                            id={country.code}
                            checked={field.value?.includes(country.code) || false}
                            onChange={(e) => {
                              const currentValue = field.value || [];
                              if (e.target.checked) {
                                field.onChange([...currentValue, country.code]);
                              } else {
                                field.onChange(currentValue.filter(code => code !== country.code));
                              }
                            }}
                            className="h-4 w-4 rounded border-gray-300"
                          />
                          <Label
                            htmlFor={country.code}
                            className="text-sm cursor-pointer truncate"
                            title={country.name}
                          >
                            {country.name}
                          </Label>
                        </div>
                      ))
                    ) : (
                      <div className="col-span-2 flex items-center justify-center py-8 text-center">
                        <div className="text-sm text-muted-foreground">
                          <div className="mb-1">No countries found</div>
                          <div className="text-xs">Try adjusting your search terms</div>
                        </div>
                      </div>
                    )}
                  </div>
                  {field.value && field.value.length > 0 && (
                    <div className="text-center font-bold">
                      <span className="text-sm text-foreground">{field.value.length === 1 ? "1 country restricted" : `${field.value.length} countries restricted`}</span>
                    </div>
                  )}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          );
        }}
      />
    </div>
  );
}
